"use client";

import { Box } from "@mui/material";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { Chapter } from "@/components/Chapter";
import { Footer } from "@/components/Footer";
import { theme } from "@/lib/theme";
import { useScrollColor, calculateColorStops, ColorStop } from "@/hooks/useScrollColor";
import Introduction from "@/sections/00-introduction/content.mdx";
import Chapter01 from "@/sections/01-finance-impacts-everyone/content.mdx";
import Chapter02 from "@/sections/02-harmful-design-versus-bad-design/content.mdx";
import Chapter03 from "@/sections/03-harmful-design-patterns-in-finance/content.mdx";
import Chapter04 from "@/sections/04-harmful-design-patterns-in-finance/content.mdx";
import Chapter05 from "@/sections/05-user-practices-and-immigrant-experience/content.mdx";
import Chapter06 from "@/sections/06-making-finance-inclusive/content.mdx";

// Define chapter colors - using the original pastel colors from the design
// Colors match the final colors in the original ChapterTitle gradients
const CHAPTER_COLORS = [
  { id: "chapter-00", color: theme.colors.white }, // #fff - white for intro
  { id: "chapter-01", color: theme.colors.lightLime }, // #F7FEE0 - light lime
  { id: "chapter-02", color: theme.colors.lightPurple }, // #F0EFFE - light purple
  { id: "chapter-03", color: theme.colors.lightLime }, // #F7FEE0 - light lime
  { id: "chapter-04", color: theme.colors.lightPurple }, // #F0EFFE - light purple
  { id: "chapter-05", color: theme.colors.darkLimeGreen }, // #D2F400 - darker green
  { id: "chapter-05-new-banks", color: theme.colors.lightLime }, // #F7FEE0 - light lime
  { id: "chapter-05-unclear-fees", color: theme.colors.lightPurple }, // #F0EFFE - section color change
  { id: "chapter-05-currency-maze", color: theme.colors.lightLime }, // #F7FEE0 - back to light lime
  { id: "chapter-06", color: theme.colors.lightPurple }, // #F0EFFE - light purple
];

export default function Home() {
  // Initialize with first chapter color to prevent flash
  const [colorStops, setColorStops] = useState<ColorStop[]>([
    {
      chapterId: "chapter-00",
      color: theme.colors.white,
      position: 0,
    },
    {
      chapterId: "chapter-00-end",
      color: theme.colors.white,
      position: 100000, // Large number to keep white until real positions load
    },
  ]);

  const backgroundColor = useScrollColor(colorStops);

  // Calculate real scroll positions after mount
  useEffect(() => {
    const updateColorStops = () => {
      const stops = calculateColorStops(CHAPTER_COLORS);
      if (stops.length > 0) {
        setColorStops(stops);
      }
    };

    // Calculate on mount and after a brief delay to ensure DOM is ready
    const timer = setTimeout(updateColorStops, 100);

    // Recalculate on window resize
    window.addEventListener("resize", updateColorStops);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", updateColorStops);
    };
  }, []);

  return (
    <>
      {/* Fixed background layer with color transitions */}
      <motion.div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0,
          backgroundColor,
        }}
      />

      <Box
        component="main"
        sx={{
          position: "relative",
          zIndex: 1,
          minHeight: "100vh",
          margin: 0,
          padding: 0,
          backgroundColor: "transparent",
        }}
      >
        {/* Content with transparent backgrounds to show the transitioning color */}
        <Chapter id="chapter-00" useGlobalBackground>
          <Introduction />
        </Chapter>

        <Chapter id="chapter-01" useGlobalBackground>
          <Chapter01 />
        </Chapter>

        <Chapter id="chapter-02" useGlobalBackground>
          <Chapter02 />
        </Chapter>

        <Chapter id="chapter-03" useGlobalBackground>
          <Chapter03 />
        </Chapter>

        <Chapter id="chapter-04" useGlobalBackground>
          <Chapter04 />
        </Chapter>

        <Chapter id="chapter-05" useGlobalBackground>
          <Chapter05 />
        </Chapter>

        <Chapter id="chapter-06" useGlobalBackground>
          <Chapter06 />
        </Chapter>

        <Footer />
      </Box>
    </>
  );
}
